import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Server, TrendingUp, Calendar, Coins, Copy, ExternalLink, LineChart as LineChartIcon, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { i18n } from '../utils/i18n';
import {
  calculateDailyReward,
  calculateTotalRewards,
  calculateExpectedRemainingRewards,
} from '../utils/stakingRewards';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { User } from '../App';

interface Node {
  id: number;
  nodeId: string;
  amount: string;
  status: string;
  startDate: string;
  daysElapsed: number;
  remainingDays: number;
  apy: number;
  dailyReward: string;
  totalRewards: string;
  expectedRemainingRewards: string;
  rewardCount: number;
  rpcUrl: string;
  p2pPort: number;
}

interface RewardGraphData {
  daily: Array<{ day: number; reward: number; total: number }>;
  weekly: Array<{ week: number; reward: number }>;
  monthly: Array<{ month: number; reward: number }>;
  summary: {
    totalReward: number;
    avgDailyReward: number;
    firstDayReward: number;
    lastDayReward: number;
  };
}

interface MyNodesProps {
  user: User;
}

// 🔥 노드 하드코딩 리스트 (20명)
const HARDCODED_NODE_WALLETS = [
  '0xe584812ec17f02d43db53921561bc8c4a7fc0aed', // 8IQS21 - 3bebe 본인
  '0xfaf754f63f6d74d4b8fe13d87231215be14d4bfc', // H3CH5G - 4bebe 재단
  '0x711da1168f1d2ed40f0e1249923335eafb942e70', // U1S8VN - 에어1004
  '0x9a798b02b2d22cf179d96362ea0dc4a06ed96368', // TCECBG - 윤소현
  '0xa9272520027522fce2f7e37e5c44efb4a90a215a', // B47FSD - tls0919
  '0x03026206a744ff2d7ada00f2d0234ae1d6a60c90', // XE8R5D - King55
  '0xdeacac228696aceb5ea73d39cb274827a910ab9d', // UK1YML - Happy7208
  '0xa87b327ec4a11f8284b1ee65f83577ecd0aa3c2d', // PFHPFE - 나르샤2801
  '0x5f5b45b64650db08e961b004f22df6fb9a7ef007', // VYUDW5 - 허1004
  '0x88651eabe4a105f91694f24c259b3264455ab60a', // RC2D03 - kjh9246
  '0x66494df36fb9d0a008a8229f3f4553735cd721a6', // L8YM61 - phs1119
  '0x03bff3d24a4f024b5145f117670b38cd41249101', // 1NQLZ8 - jtk0770
  '0xb1ee966f7d9ba51d6f7a7735ca70823bf6827933', // GB6MMO - pcy7504
  '0x369e0f13626d3f25886dba04bf3c7a504ee18fc3', // RHM8XH - lim99k (김원갑)
  '0x921b665f5862eb0773350c4f834379fc8cb7e715', // QYUCEG - imactorone
  '0x5ebbb334ef12b0cea723e101efc93b62f2e3ae29', // 4VLJR8 - les4243 (이은순)
  '0xe5f39ba195de569f3ff73c8f566bffce77494fc1', // C46WDW - hero777 (장미순)
  '0xdaaf4ff911499cc1e037d3bfa4e47c87ae583209', // 9AZP36 - 정현미
  '0x331Ef020F1019D8316e6D95D63a34aC8aE8e0a5A', // 6EXUGF - s7052 (김정숙)
  '0x2222F44FD1CcB79Dc63c27BCCaC214fB95ff5c1B', // P3QZW1 - k5893 (김연해)
  '0x3aa4640301fa0aad18f380499482274ad0d2ad5d', // DT2YN3 - MDPR
];

// 🎛️ 토글: false = 본인만, true = 전체 13명
const SHOW_ALL_HARDCODED_NODES = false;

export function MyNodes({ user }: MyNodesProps) {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [graphData, setGraphData] = useState<RewardGraphData | null>(null);
  const [graphView, setGraphView] = useState<'daily' | 'weekly' | 'monthly'>('weekly');

  useEffect(() => {
    loadNodes();
  }, []);

  const loadNodes = async () => {
    try {
      // 🚨 로컬스토리지에서 직접 walletAddress 확인 (user 객체 불필요!)
      const walletAddress = localStorage.getItem('vatton-wallet-address');
      
      console.log('🔍 저장된 walletAddress:', walletAddress);
      
      // 🔥 하드코딩 노드 체크 (13명)
      const normalizedWallet = walletAddress?.toLowerCase();
      
      // 13명 전체 표시
      const shouldShowHardcodedNode = HARDCODED_NODE_WALLETS.includes(normalizedWallet || '');
      
      if (shouldShowHardcodedNode) {
        console.log('✅ 하드코딩 노드 표시!', walletAddress);
        
        // 🔥 자동 계산: 시작일로부터 경과일 및 수익 계산
        const startDate = new Date('2025-11-02T14:00:00.000Z'); // 2025-11-02 오후 2시 (KST 기준 23:00)
        const now = new Date();
        const diffTime = Math.max(0, now.getTime() - startDate.getTime());
        const totalDaysElapsed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const daysElapsed = Math.min(totalDaysElapsed, 365);
        const remainingDays = Math.max(365 - daysElapsed, 0);

        const dayNumber = Math.min(daysElapsed + 1, 365);
        const stakingAmount = 120000;

        const dailyRewardValue = calculateDailyReward({ stakingAmount, dayNumber });
        const totalRewardsValue = calculateTotalRewards({ stakingAmount, daysElapsed });
        const expectedRemainingRewardsValue = calculateExpectedRemainingRewards({
          stakingAmount,
          daysElapsed,
        });
        
        console.log(`📅 경과일: ${daysElapsed}일, 총 수익: ${totalRewardsValue} VTTN`);
        
        const hardcodedNodes: Node[] = [
          {
            id: 1,
            nodeId: 'VATTON_NODE_20251102',
            amount: '120000.000000000000000000',
            status: 'active',
            startDate: '2025-11-02T14:00:00.000Z',
            daysElapsed: daysElapsed,
            remainingDays: remainingDays,
            apy: 32,
            dailyReward: dailyRewardValue.toFixed(2),
            totalRewards: totalRewardsValue.toFixed(2),
            expectedRemainingRewards: expectedRemainingRewardsValue.toFixed(2),
            rewardCount: daysElapsed,
            rpcUrl: 'http://159.223.63.184:10003',
            p2pPort: 10002
          }
        ];
        setNodes(hardcodedNodes);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('vatton-jwt-token');
      const response = await fetch('/api/nodes/my-nodes', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setNodes(result.data);
      } else {
        console.log('노드 목록 조회 실패');
      }
    } catch (error) {
      console.error('노드 목록 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRewardsGraph = async (nodeId: number) => {
    try {
      const token = localStorage.getItem('vatton-jwt-token');
      const response = await fetch(`/api/nodes/rewards-graph/${nodeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        setGraphData(result.data);
        setSelectedNodeId(nodeId);
      }
    } catch (error) {
      console.error('그래프 데이터 로드 오류:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('복사되었습니다');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="text-center py-12">
        <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">노드가 없습니다</h3>
        <p className="text-gray-500 mb-6">스테이킹 메뉴에서 노드를 구독하세요</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{user?.nickname || localStorage.getItem('vatton-nickname') || '사용자'}의 바톤노드</h2>
        <button
          onClick={loadNodes}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
        >
          새로고침
        </button>
      </div>

      {nodes.map((node) => (
        <Card key={node.id} className="border-2 border-gray-200">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* 헤더 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-gray-500">노드 ID</h3>
                    <p className="font-bold text-lg text-gray-900">{node.nodeId}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      P2P Port ({node.nodeId.includes('VATTON_NODE') ? 'fullnode-1' : node.nodeId})
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    활성
                  </span>
                </div>
              </div>

              {/* 통계 그리드 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Coins className="w-4 h-4 text-blue-600" />
                    <p className="text-xs text-blue-600 font-medium">스테이킹</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{parseFloat(node.amount).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">VTTN</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <p className="text-xs text-green-600 font-medium">APY</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{node.apy}%</p>
                  <p className="text-xs text-gray-500">연 수익률</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <p className="text-xs text-purple-600 font-medium">일별 수익</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{node.dailyReward}</p>
                  <p className="text-xs text-gray-500">VTTN/일 (≈ ${(parseFloat(node.dailyReward) * 0.5).toFixed(2)})</p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Coins className="w-4 h-4 text-orange-600" />
                    <p className="text-xs text-orange-600 font-medium">총 수익</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{node.totalRewards}</p>
                  <p className="text-xs text-gray-500">VTTN (≈ ${(parseFloat(node.totalRewards) * 0.5).toFixed(2)})</p>
                </div>
              </div>

              {/* 진행률 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">진행률</span>
                  <span className="text-sm text-gray-600">
                    {node.daysElapsed}일 / 365일
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                    style={{ width: `${(node.daysElapsed / 365) * 100}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>시작: {new Date(node.startDate).toLocaleDateString('ko-KR')}</span>
                  <span>남은 기간: {node.remainingDays}일</span>
                </div>
              </div>

              {/* 수익 상세 */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold text-gray-900 mb-3">수익 상세</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">지급된 수익</p>
                    <p className="font-semibold text-gray-900">{node.totalRewards} VTTN</p>
                    <p className="text-xs text-gray-500">≈ ${(parseFloat(node.totalRewards) * 0.5).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">지급 횟수</p>
                    <p className="font-semibold text-gray-900">{node.rewardCount}회</p>
                  </div>
                  <div>
                    <p className="text-gray-600">예상 남은 수익</p>
                    <p className="font-semibold text-gray-900">{parseFloat(node.expectedRemainingRewards).toLocaleString()} VTTN</p>
                    <p className="text-xs text-gray-500">≈ ${(parseFloat(node.expectedRemainingRewards) * 0.5).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">예상 총 수익</p>
                    <p className="font-semibold text-green-600">
                      {(parseFloat(node.totalRewards) + parseFloat(node.expectedRemainingRewards)).toLocaleString()} VTTN
                    </p>
                    <p className="text-xs text-green-600">≈ ${((parseFloat(node.totalRewards) + parseFloat(node.expectedRemainingRewards)) * 0.5).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* 노드 정보 */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold text-gray-900 mb-3">노드 정보</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">RPC URL</span>
                    <div className="flex items-center space-x-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                        {node.rpcUrl}
                      </code>
                      <button
                        onClick={() => copyToClipboard(node.rpcUrl)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">P2P Port</span>
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                      {node.p2pPort}
                    </code>
                  </div>
                </div>
              </div>

              {/* 수익률 그래프 */}
              {selectedNodeId === node.id && graphData && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">수익률 추이 (반감기 모델)</h4>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setGraphView('daily')}
                        className={`px-3 py-1 rounded text-sm ${graphView === 'daily' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        일별
                      </button>
                      <button
                        onClick={() => setGraphView('weekly')}
                        className={`px-3 py-1 rounded text-sm ${graphView === 'weekly' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        주별
                      </button>
                      <button
                        onClick={() => setGraphView('monthly')}
                        className={`px-3 py-1 rounded text-sm ${graphView === 'monthly' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        월별
                      </button>
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={250}>
                    {graphView === 'daily' ? (
                      <AreaChart data={graphData.daily.filter((_, i) => i % 7 === 0)}>
                        <defs>
                          <linearGradient id="colorReward" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.2}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          labelFormatter={(value) => `${value}일차`}
                          formatter={(value: any) => [`${parseFloat(value).toFixed(2)} VTTN`, '일일 보상']}
                        />
                        <Area type="monotone" dataKey="reward" stroke="#10b981" fillOpacity={1} fill="url(#colorReward)" />
                      </AreaChart>
                    ) : graphView === 'weekly' ? (
                      <BarChart data={graphData.weekly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="week" stroke="#6b7280" fontSize={12} label={{ value: '주', position: 'insideBottom', offset: -5 }} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          labelFormatter={(value) => `${value}주차`}
                          formatter={(value: any) => [`${parseFloat(value).toFixed(2)} VTTN`, '주간 보상']}
                        />
                        <Bar dataKey="reward" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    ) : (
                      <BarChart data={graphData.monthly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" fontSize={12} label={{ value: '월', position: 'insideBottom', offset: -5 }} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          labelFormatter={(value) => `${value}개월`}
                          formatter={(value: any) => [`${parseFloat(value).toFixed(2)} VTTN`, '월간 보상']}
                        />
                        <Bar dataKey="reward" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>

                  <div className="grid grid-cols-4 gap-2 mt-4 text-xs">
                    <div className="bg-green-50 p-2 rounded">
                      <p className="text-gray-600">초기 보상</p>
                      <p className="font-bold text-green-600">{graphData.summary.firstDayReward.toFixed(2)} VTTN</p>
                    </div>
                    <div className="bg-orange-50 p-2 rounded">
                      <p className="text-gray-600">최종 보상</p>
                      <p className="font-bold text-orange-600">{graphData.summary.lastDayReward.toFixed(2)} VTTN</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded">
                      <p className="text-gray-600">평균 보상</p>
                      <p className="font-bold text-blue-600">{graphData.summary.avgDailyReward.toFixed(2)} VTTN</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded">
                      <p className="text-gray-600">1년 총액</p>
                      <p className="font-bold text-purple-600">{graphData.summary.totalReward.toFixed(2)} VTTN</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 액션 버튼 */}
              <div className="flex space-x-3 pt-2">
                <button 
                  onClick={() => selectedNodeId === node.id ? setSelectedNodeId(null) : loadRewardsGraph(node.id)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2"
                >
                  <LineChartIcon className="w-4 h-4" />
                  <span>{selectedNodeId === node.id ? '그래프 숨기기' : '수익률 그래프'}</span>
                </button>
                <button className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium">
                  수익 내역
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

